// The take-into-work gate for a matter born from intake - the AI
// interview and the client questionnaire land here alike. The
// server claim is atomic; whatever it answers, the matter query is
// refetched so the banner reflects who actually won.

import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { caseSignalsKey } from "../../hooks/useCaseSignals";
import { claimMatter, ClaimTakenError } from "../../api/claim";

export function ReviewBanner({ caseId, eID }: { caseId: string; eID: string }) {
  const queryClient = useQueryClient();
  const [taken, setTaken] = useState(false);
  // isPending stays false until the next render; the ref stops a second
  // dispatch that lands before React re-renders the disabled button.
  const inFlight = useRef(false);
  const claim = useMutation({
    mutationFn: () => claimMatter(caseId),
    onError: (err) => {
      if (err instanceof ClaimTakenError) setTaken(true);
    },
    onSettled: () => {
      inFlight.current = false;
      queryClient.invalidateQueries({ queryKey: ["matter", eID] });
      queryClient.invalidateQueries({ queryKey: ["matters"] });
      queryClient.invalidateQueries({ queryKey: ["firm", "counts"] });
      // Taken into work, the matter starts to owe things: its signals speak now.
      queryClient.invalidateQueries({ queryKey: caseSignalsKey(caseId) });
    },
  });
  const take = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    claim.mutate();
  };

  // The banner outlives the win: it unmounts only when the matter refetch
  // lands. Until then the button must stay down and say so, or a second
  // click would tell the winner someone else took it.
  const done = claim.isSuccess;
  return (
    <div
      className="cc-card"
      // Amber outline like DuplicateNotice: a pending state in its status color.
      style={{ padding: "12px 18px", marginBottom: 12, border: "1px solid var(--warn-600)" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <span>
          <b style={{ color: "var(--warn-600)" }}>Awaiting review</b> This matter came in through
          intake and nobody has vetted it yet.
        </span>
        <button
          type="button"
          className="cc-btn-primary"
          disabled={claim.isPending || done}
          onClick={take}
        >
          {done ? "Taken" : claim.isPending ? "Taking..." : "Take into work"}
        </button>
      </div>
      {done && (
        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 8 }}>
          The matter is yours - refreshing the page data.
        </div>
      )}
      {taken && (
        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 8 }}>
          Someone already took this matter - the page has been refreshed.
        </div>
      )}
      {claim.isError && !taken && (
        <div style={{ fontSize: 11, color: "var(--red-700)", marginTop: 8 }}>
          Could not take the matter - try again.
        </div>
      )}
    </div>
  );
}
